import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import Layout from '../../components/layout/Layout';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';

const EMPTY_FORM = { nome: '', cidade: '', estado: '', endereco: '' };
const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const Lojas = () => {
  const [lojas, setLojas]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editando, setEditando]     = useState(null);
  const [deletando, setDeletando]   = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const fetchLojas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/lojas');
      setLojas(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLojas(); }, []);

  const abrirNovo = () => {
    setEditando(null);
    setForm(EMPTY_FORM);
    setError('');
    setModalOpen(true);
  };

  const abrirEditar = (l) => {
    setEditando(l);
    setForm({ nome: l.nome, cidade: l.cidade || '', estado: l.estado || '', endereco: l.endereco || '' });
    setError('');
    setModalOpen(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editando) {
        await api.put(`/lojas/${editando.id}`, form);
      } else {
        await api.post('/lojas', form);
      }
      setModalOpen(false);
      fetchLojas();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar loja.');
    } finally {
      setSaving(false);
    }
  };

  const confirmarDelete = async () => {
    if (!deletando) return;
    try {
      await api.delete(`/lojas/${deletando.id}`);
      setLojas((prev) => prev.filter((l) => l.id !== deletando.id));
      setDeletando(null);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Lojas</h1>
          <p className="text-sm text-gray-500 mt-0.5">{lojas.length} loja(s)</p>
        </div>
        <button onClick={abrirNovo} className="btn-primary">
          <PlusIcon className="w-4 h-4" />
          Nova loja
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : lojas.length === 0 ? (
        <EmptyState title="Nenhuma loja cadastrada" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lojas.map((l) => (
            <div key={l.id} className="card p-5 flex flex-col gap-2">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-gray-900">{l.nome}</h3>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => abrirEditar(l)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeletando(l)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {(l.cidade || l.estado) && (
                <p className="text-sm text-gray-500">
                  {[l.cidade, l.estado].filter(Boolean).join(' — ')}
                </p>
              )}
              {l.endereco && <p className="text-xs text-gray-400">{l.endereco}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Modal criar/editar */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editando ? 'Editar loja' : 'Nova loja'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome <span className="text-red-500">*</span></label>
            <input name="nome" value={form.nome} onChange={handleChange} required className="input" placeholder="Ex: Simmons Centro" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
              <input name="cidade" value={form.cidade} onChange={handleChange} className="input" placeholder="São Paulo" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select name="estado" value={form.estado} onChange={handleChange} className="input">
                <option value="">UF</option>
                {ESTADOS_BR.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
            <input name="endereco" value={form.endereco} onChange={handleChange} className="input" placeholder="Rua, número, bairro" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Salvando...' : editando ? 'Salvar' : 'Criar loja'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de exclusão */}
      <Modal open={!!deletando} onClose={() => setDeletando(null)} title="Remover loja">
        <p className="text-sm text-gray-600 mb-6">
          Tem certeza que deseja remover a loja <strong>{deletando?.nome}</strong>?
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setDeletando(null)} className="btn-secondary">Cancelar</button>
          <button onClick={confirmarDelete} className="btn-danger">Remover</button>
        </div>
      </Modal>
    </Layout>
  );
};

export default Lojas;
